import { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { resolveQrForCompany, logQrScanActivity } from '@/lib/qr-utils';
import QrScanner from 'qr-scanner';

export interface ProductData {
  id: string;
  name: string;
  item_code: string;
  price: number;
  quantity_on_hand: number;
  category: string;
}

interface UseWorkingQRScanResult {
  isScanning: boolean;
  hasPermission: boolean | null;
  flashEnabled: boolean;
  cameras: QrScanner.Camera[];
  currentCameraId: string;
  error: string;
  companyId: string | null;
  authLoading: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  startScanning: () => Promise<void>;
  stopScanning: () => void;
  toggleFlash: () => Promise<void>;
  switchCamera: () => Promise<void>;
}

export function useWorkingQRScan(
  onProductFound: (product: ProductData) => void
): UseWorkingQRScanResult {
  const { toast } = useToast();
  const { companyId, loading: authLoading } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [cameras, setCameras] = useState<QrScanner.Camera[]>([]);
  const [currentCameraId, setCurrentCameraId] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Debug auth state
  useEffect(() => {
    console.log('🔍 WorkingQRScan - Auth state:', { companyId, authLoading });
    if (!authLoading && !companyId) {
      setError('Şirket bilgileri yüklenemedi');
    } else if (!authLoading && companyId) {
      setError(''); // Clear any previous auth errors
    }
  }, [companyId, authLoading]);

  // Initialize camera and QR scanner
  useEffect(() => {
    let mounted = true;

    const initializeScanner = async () => {
      try {
        // Check if camera is available
        const hasCamera = await QrScanner.hasCamera();
        if (!hasCamera) {
          setError("Kamera bulunamadı");
          return;
        }

        // Get available cameras
        const availableCameras = await QrScanner.listCameras(true);
        if (mounted) {
          setCameras(availableCameras);
          // Prefer back camera for mobile devices
          const backCamera = availableCameras.find(camera => 
            camera.label.toLowerCase().includes('back') || 
            camera.label.toLowerCase().includes('rear') ||
            camera.label.toLowerCase().includes('environment')
          );
          const preferredCamera = backCamera || availableCameras[0];
          if (preferredCamera) {
            setCurrentCameraId(preferredCamera.id);
          }
        }
      } catch (err) {
        console.error("Error initializing scanner:", err);
        if (mounted) {
          setError("Kamera erişim hatası");
        }
      }
    };

    initializeScanner();

    return () => {
      mounted = false;
      stopScanning();
    };
  }, []);

  // Handle page visibility change (iOS Safari optimization)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isScanning) {
        // Pause scanning when page becomes hidden
        stopScanning();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isScanning]);

  const startScanning = useCallback(async () => {
    if (!videoRef.current) return;

    try {
      setIsScanning(true);
      setError("");

      // Create QR scanner instance
      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => handleScanResult(result.data),
        {
          onDecodeError: (err) => {
            // Silently handle decode errors (normal when no QR code is visible)
          },
          preferredCamera: currentCameraId || 'environment',
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 5,
        }
      );

      // Set video constraints for mobile optimization
      const constraints = {
        facingMode: 'environment', // Use back camera by default
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 }
      };

      await qrScannerRef.current.start();
      setHasPermission(true);
    } catch (err: any) {
      console.error("Error starting scanner:", err);
      setIsScanning(false);
      if (err.name === 'NotAllowedError') {
        setHasPermission(false);
        setError("Kamera erişim izni gerekli");
      } else if (err.name === 'NotFoundError') {
        setError("Kamera bulunamadı");
      } else if (err.name === 'NotSupportedError' || err.message?.includes('https')) {
        setError("HTTPS bağlantısı gerekli - Tarayıcı güvenlik ayarlarını kontrol edin");
      } else if (err.name === 'OverconstrainedError') {
        setError("Kamera ayarları uyumlu değil");
      } else {
        setError(`Kamera başlatılamadı: ${err.message || err.name || 'Bilinmeyen hata'}`);
      }
    }
  }, [currentCameraId]);

  const stopScanning = useCallback(() => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    setIsScanning(false);
  }, []);

  const handleScanResult = useCallback(async (data: string) => {
    console.log('🔍 WorkingQRScan - QR scan result received:', data);
    console.log('🔍 WorkingQRScan - Current companyId state:', companyId);
    console.log('🔍 WorkingQRScan - authLoading state:', authLoading);
    
    stopScanning();
    
    if (!companyId) {
      console.error('🔍 WorkingQRScan - No company ID available during scan');
      toast({
        title: "Hata",
        description: "Şirket bilgileri yüklenmedi",
        variant: "destructive"
      });
      return;
    }
    
    console.log('📱 Working QR Scanner - Scanned data:', data);
    console.log('📱 Working QR Scanner - Company ID:', companyId);
    
    toast({
      title: "QR Kod Başarıyla Tarandı!",
      description: "Ürün bilgileri yükleniyor...",
    });
    
    // Use the universal QR resolver
    const result = await resolveQrForCompany(data, companyId);
    console.log('📱 Working QR Scanner - Resolution result:', result);

    // Log the scan activity for analytics
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await logQrScanActivity(data, user.id, companyId, result);
    }

    // Handle successful scan
    if (result.success && result.productId) {
      // Fetch full product details
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('id, name, item_code, price, quantity_on_hand, category')
        .eq('id', result.productId)
        .eq('company_id', companyId)
        .single();

      if (productError || !product) {
        toast({
          title: "Hata",
          description: "Ürün bilgileri alınamadı",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Ürün Eklendi!",
        description: `${product.name} başarıyla eklendi`,
      });

      // Call the callback with product data
      onProductFound(product);
    } else {
      toast({
        title: result.type === 'not_in_company' ? "Ürün Mevcut Değil" : 
               result.type === 'not_found' ? "Ürün Bulunamadı" : "QR Kod Hatası",
        description: result.message,
        variant: "destructive",
        duration: result.type === 'not_in_company' ? 8000 : 5000
      });
    }
  }, [companyId, onProductFound, stopScanning, toast]);

  const toggleFlash = useCallback(async () => {
    if (qrScannerRef.current) {
      try {
        if (flashEnabled) {
          await qrScannerRef.current.turnFlashOff();
        } else {
          await qrScannerRef.current.turnFlashOn();
        }
        setFlashEnabled(!flashEnabled);
      } catch (err) {
        console.error("Flash toggle error:", err);
        toast({
          title: "Flaş Hatası",
          description: "Flaş kontrolü desteklenmiyor",
          variant: "destructive"
        });
      }
    }
  }, [flashEnabled, toast]);

  const switchCamera = useCallback(async () => {
    if (cameras.length <= 1) return;

    const currentIndex = cameras.findIndex(camera => camera.id === currentCameraId);
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];

    setCurrentCameraId(nextCamera.id);

    if (qrScannerRef.current) {
      try {
        await qrScannerRef.current.setCamera(nextCamera.id);
      } catch (err) {
        console.error("Camera switch error:", err);
      }
    }
  }, [cameras, currentCameraId]);

  return {
    isScanning,
    hasPermission,
    flashEnabled,
    cameras,
    currentCameraId,
    error,
    companyId,
    authLoading,
    videoRef,
    startScanning,
    stopScanning,
    toggleFlash,
    switchCamera
  };
}
